import {Component, effect, inject, signal} from '@angular/core';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {LineChartModule} from "@swimlane/ngx-charts";
import {NgClass} from "@angular/common";
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {WaterPointSvgComponent} from "src/app/presentation/common/icons/water-point-svg/water-point-svg.component";
import {DeviceAssociation, SynchronizationStatus, WaterPoint} from "src/app/infrastructure/moovement-api/models/asset";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {CustomizableAssetComponent} from "src/app/presentation/common/customizable-asset.component";
import {InternalNotificationType} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {WaterPointsStore} from 'src/app/domain/assets/water-points/water-points.store';
import {parseOptionalFloat, parseOptionalInt} from 'src/app/domain/util/parsing/parsing';
import {InfoButtonComponent} from "src/app/presentation/common/info-button/info-button.component";
import {
  datumInfo,
  highAlertInfo,
  highWarningInfo,
  lowAlertInfo,
  lowWarningInfo,
  nominalValueInfo
} from 'src/app/domain/assets/water-points/water-point';

@Component({
  selector: 'app-customize-water-sample-point',
  imports: [
    GenericButtonComponent,
    LineChartModule,
    SpinnerComponent,
    WaterPointSvgComponent,
    NgClass,
    InfoButtonComponent
],
  templateUrl: './customize-water-sample-point.component.html',
  styleUrl: './customize-water-sample-point.component.css'
})
export class CustomizeWaterSamplePointComponent extends CustomizableAssetComponent<WaterPoint> {

  dropAPinEnabled: boolean = true
  deviceAssociationAltered: boolean = false

  nominalValueInfo = signal(nominalValueInfo);
  datumInfo = signal(datumInfo);
  highAlertInfo = signal(highAlertInfo);
  highWarningInfo = signal(highWarningInfo);
  lowWarningInfo = signal(lowWarningInfo);
  lowAlertInfo = signal(lowAlertInfo);

  deviceAssociationWithThresholds = signal<DeviceAssociation | undefined>(undefined);

  private waterPointsStore = inject(WaterPointsStore);

  constructor(dataSynchronisationService: DataSynchronisationService, dataSubscriptionService: DataSubscriptionService, viewportService: ViewportService) {
    super(dataSynchronisationService, dataSubscriptionService, viewportService);

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.asset.set(this.waterPointsStore.entityMap()[assetId]);
    });

    effect(() => {
      const deviceAssociations = this.deviceAssociations();
      if (deviceAssociations.length === 1) {
        this.deviceAssociationWithThresholds.set(deviceAssociations[0]);
      } else {
        this.deviceAssociationWithThresholds.set(this.deviceAssociations().find(deviceAssociation => deviceAssociation.sampleThresholds));
      }
    });
  }

  override confirmDropAPinMessage(): string {
    return "Are you sure you want to change the location of this water point?";
  }

  setName($event: any): void {
    this.asset.update(asset => {
      if (!asset) return undefined;

      return {
        ...asset,
        name: $event.target.value
      }
    });
  }

  setLatitude($event: any): void {
    const latitude = parseOptionalFloat($event.target.value);
    this.updateLocation(latitude, this.asset()?.location?.coords?.[1]);
  }

  setLongitude($event: any) {
    const longitude = parseOptionalFloat($event.target.value);
    this.updateLocation(this.asset()?.location?.coords?.[0], longitude);
  }

  private updateLocation(latitude?: number, longitude?: number) {
    if (!latitude || !longitude) return;

    this.asset.update(asset => {
      if (!asset) return undefined;

      return {
        ...asset,
        location: {
          ...asset.location,
          coords: [latitude, longitude]
        }
      }
    });
  }

  setHighAlert($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          thresholds: {
            ...deviceAssociation?.sampleThresholds?.WaterLevel?.thresholds,
            "HIGH_ALERT": parseOptionalInt($event.target.value)
          }
        }
      }
    } as DeviceAssociation));
  }

  setHighWarning($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          thresholds: {
            ...deviceAssociation?.sampleThresholds?.WaterLevel?.thresholds,
            "HIGH_WARNING": parseOptionalInt($event.target.value)
          }
        }
      }
    } as DeviceAssociation));
  }

  setLowWarning($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          thresholds: {
            ...deviceAssociation?.sampleThresholds?.WaterLevel?.thresholds,
            "LOW_WARNING": parseOptionalInt($event.target.value)
          }
        }
      }
    } as DeviceAssociation));
  }

  setLowAlert($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          thresholds: {
            ...deviceAssociation?.sampleThresholds?.WaterLevel?.thresholds,
            "LOW_ALERT": parseOptionalInt($event.target.value)
          }
        }
      }
    } as DeviceAssociation));
  }

  setDatum($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          datum: parseOptionalInt($event.target.value),
        }
      }
    } as DeviceAssociation));
  }

  setNominalValue($event: any) {
    this.deviceAssociationAltered = true
    this.deviceAssociationWithThresholds.update(deviceAssociation => ({
      ...deviceAssociation,
      sampleThresholds: {
        ...deviceAssociation?.sampleThresholds,
        WaterLevel: {
          ...deviceAssociation?.sampleThresholds?.WaterLevel,
          nominalValue: parseOptionalInt($event.target.value),
        }
      }
    } as DeviceAssociation));
  }

  override async applyChanges() {
    if (!this.loading && this.canBeAltered) {
      await this.dataSynchronisationService.applyAction(
        DataType.waterPoints,
        ActionType.mergeAsset,
        this.asset()!,
        SynchronizationStatus.syncPending
      );

      const deviceAssociationWithThresholds = this.deviceAssociationWithThresholds();
      if (deviceAssociationWithThresholds && this.deviceAssociationAltered) {
        await this.dataSynchronisationService.applyAction(
          DataType.deviceAssociations,
          ActionType.mergeAsset,
          deviceAssociationWithThresholds,
          SynchronizationStatus.syncPending
        );
      }

      this.internalNotificationsStore.addNotification("Successfully altered water point.", InternalNotificationType.SUCCESS, false);
    }
  }
}
